import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  computeFittedStageSize,
  TOOL_PREP_STAGE_INSET,
} from "../../../lib/toolPrepImageLayout";
import RoundLoader from "../../../../../components/ui/RoundLoader";
import ToolPrepCanvasStage from "../../../shared/Popup/ToolPrepCanvasStage";
import "./OutpaintPreview.css";

/**
 * Preview of outpaint expansion: original image centered with gutters for new areas.
 */
const OutpaintPreview = ({
  imageUrl = null,
  naturalW = 0,
  naturalH = 0,
  expand = { left: 0, right: 0, up: 0, down: 0 },
  loading = false,
  applying = false,
  resultMode = false,
  emptyLabel = "Upload an image to preview expand",
}) => {
  const viewportRef = useRef(null);
  const [viewport, setViewport] = useState({ w: 420, h: 420 });
  const [emptyStageSize, setEmptyStageSize] = useState({
    width: 420,
    height: 420,
  });

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    const measure = () => {
      const w = Math.max(120, el.clientWidth);
      const h = Math.max(120, el.clientHeight);
      setViewport({ w, h });
      const fitted = computeFittedStageSize(w, h, 1, {
        inset: TOOL_PREP_STAGE_INSET,
      });
      if (fitted) setEmptyStageSize(fitted);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const layout = useMemo(() => {
    if (!naturalW || !naturalH) return null;
    const outW = naturalW + (expand.left || 0) + (expand.right || 0);
    const outH = naturalH + (expand.up || 0) + (expand.down || 0);
    if (outW <= 0 || outH <= 0) return null;
    const fitted = computeFittedStageSize(viewport.w, viewport.h, outW / outH, {
      inset: TOOL_PREP_STAGE_INSET,
    });
    if (!fitted) return null;
    const fit = fitted.width / outW;
    return {
      totalW: fitted.width,
      totalH: fitted.height,
      imgW: naturalW * fit,
      imgH: naturalH * fit,
      gL: (expand.left || 0) * fit,
      gR: (expand.right || 0) * fit,
      gU: (expand.up || 0) * fit,
      gD: (expand.down || 0) * fit,
      outW,
      outH,
    };
  }, [naturalW, naturalH, expand, viewport]);

  return (
    <ToolPrepCanvasStage
      className="otp-preview"
      ref={viewportRef}
      metaImageUrl={imageUrl}
      metaWidth={layout?.outW || naturalW}
      metaHeight={layout?.outH || naturalH}
    >
      {loading ? (
        <div className="otp-preview__loader">
          <RoundLoader />
          <p>{applying ? "Opening in editor…" : "Expanding image…"}</p>
        </div>
      ) : null}
      {!imageUrl ? (
        <div
          className="otp-preview__empty-stage"
          style={{
            width: emptyStageSize.width,
            height: emptyStageSize.height,
          }}
          aria-label="Empty preview"
        >
          <p className="otp-preview__empty">{emptyLabel}</p>
        </div>
      ) : layout ? (
        <div
          className={`otp-preview__frame${
            resultMode ? " otp-preview__frame--result" : ""
          } otp-preview__frame--checker`}
          style={{ width: layout.totalW, height: layout.totalH }}
          aria-label={
            resultMode
              ? `Result ${layout.outW}×${layout.outH}`
              : `Expanded canvas ${layout.outW}×${layout.outH}`
          }
        >
          {!resultMode && (expand.up || 0) > 0 ? (
            <div
              className="otp-preview__gutter otp-preview__gutter--up"
              style={{ height: layout.gU }}
            />
          ) : null}
          <div className="otp-preview__mid-row">
            {!resultMode && (expand.left || 0) > 0 ? (
              <div
                className="otp-preview__gutter otp-preview__gutter--left"
                style={{ width: layout.gL }}
              />
            ) : null}
            <img
              className="otp-preview__image"
              src={imageUrl}
              alt={resultMode ? "Outpaint result" : "Outpaint preview"}
              style={{ width: layout.imgW, height: layout.imgH }}
              draggable={false}
            />
            {!resultMode && (expand.right || 0) > 0 ? (
              <div
                className="otp-preview__gutter otp-preview__gutter--right"
                style={{ width: layout.gR }}
              />
            ) : null}
          </div>
          {!resultMode && (expand.down || 0) > 0 ? (
            <div
              className="otp-preview__gutter otp-preview__gutter--down"
              style={{ height: layout.gD }}
            />
          ) : null}
        </div>
      ) : (
        <img className="otp-preview__fallback-img" src={imageUrl} alt="Preview" />
      )}
    </ToolPrepCanvasStage>
  );
};

export default OutpaintPreview;
